import { type TaskResponse } from '../../client';
import { useTeam, useSimulationStatus } from '../../hooks';

import { TaskCard, TaskCardDice, TaskCardEquationSolver, TaskCardMemoryGame } from '../molecules';

interface Props {
  task: TaskResponse;
  onTaskProgress: (task: TaskResponse, progress: number) => Promise<void>;
  onTaskStarted: (task: TaskResponse) => Promise<void>;
  onTaskCompleted: (task: TaskResponse) => Promise<void>;
}

export function TaskView({ task, onTaskProgress, onTaskStarted, onTaskCompleted }: Props) {
  const team = useTeam();
  const simulationStatus = useSimulationStatus();

  if (task.team_name !== team.name) {
    return <TaskOfAnotherTeam task={task} />;
  }

  if (simulationStatus.state === 'paused') {
    return <TaskDuringPause task={task} />;
  }

  if (task.type === 'memory') {
    return (
      <TaskCardMemoryGame
        task={task}
        onTaskStarted={onTaskStarted}
        onTaskProgress={onTaskProgress}
        onTaskCompleted={onTaskCompleted}
      />
    );
  } else if (task.type === 'equation') {
    return (
      <TaskCardEquationSolver
        task={task}
        onTaskStarted={onTaskStarted}
        onTaskProgress={onTaskProgress}
        onTaskCompleted={onTaskCompleted}
      />
    );
  }

  return (
    <TaskCardDice
      task={task}
      onTaskStarted={onTaskStarted}
      onTaskProgress={onTaskProgress}
      onTaskCompleted={onTaskCompleted}
    />
  );
}

function TaskOfAnotherTeam({ task }: { task: TaskResponse }) {
  return (
    <TaskCard task={task} inaccessible>
      <div className="max-w-[70vw] p-2">
        <div className="flex items-start gap-3 p-3 rounded-md bg-feedback-warning/10 border-l-4 border-feedback-warning">
          <span className="text-feedback-warning text-lg">⚠</span>
          <p className="text-sm">This is another team's task, you are not allowed to work on it.</p>
        </div>
      </div>
    </TaskCard>
  );
}

function TaskDuringPause({ task }: { task: TaskResponse }) {
  return (
    <TaskCard task={task} inaccessible>
      <div className="max-w-[70vw] p-2">
        <div className="flex items-start gap-3 p-3 rounded-md bg-feedback-info/10 border-l-4 border-feedback-info">
          <span className="text-feedback-info text-lg">ℹ</span>
          <p className="text-sm">The simulation is currently paused. You can review this task but cannot work on it.</p>
        </div>
      </div>
    </TaskCard>
  );
}
